import { startTransition, useCallback, useMemo } from "react"

import includes from "lodash-es/includes"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { PRODUCT_TYPES_WITH_ENROLLMENTS } from "@/3514/config"
import { EPath } from "@/3514/constants"
import { type TUserState, useUser } from "@/3514/hooks"
import { EProductTypeInternalName } from "@/3514/types"
import { isUserAssistant, isUserCoach, isUserManager, isUserParticipant } from "@/3514/utils"
import { INDEX_URL, ParticipantUrls } from "@/constants"
import type { TEmptyCallback } from "@/shared/types/functions"

type TUseLogoNavigation = {
    handleLogoNavigation: TEmptyCallback
    logoNavigationUrl: string
}

function useLogoNavigation(): ReturnType<() => TUseLogoNavigation> {
    const navigate: NavigateFunction = useNavigate()

    const { user }: TUserState = useUser()

    const logoNavigationUrl: string = useMemo((): string => {
        let url: string = INDEX_URL

        if (isUserAssistant(user)) {
            url = EPath.Assistant_Participants()
        } else if (isUserCoach(user) || isUserManager(user)) {
            url = EPath.Coach_Dashboard({ tab: "Sessions" })
        } else if (isUserParticipant(user)) {
            if (user.areNotCompletedSurveysPresent && user.isOnboardingFinished) {
                url = ParticipantUrls.SURVEYS
            } else if (
                user.isOnboardingFinished &&
                user.areModulesAvailable &&
                includes<EProductTypeInternalName>(PRODUCT_TYPES_WITH_ENROLLMENTS, user?.productType)
            ) {
                url = EPath.Participant_Dashboard()
            } else if (!user.doesCohortExist) {
                url = ParticipantUrls.WAITING_LIST
            } else {
                url = EPath.Participant_CoachingSessions()
            }
        }

        return url
    }, [user])

    const handleLogoNavigation: TEmptyCallback = useCallback(
        (): void => startTransition((): void => void navigate(logoNavigationUrl)),
        [navigate, logoNavigationUrl]
    )

    return { handleLogoNavigation, logoNavigationUrl }
}

export { useLogoNavigation, type TUseLogoNavigation }
